import { useResizeObserver } from '@ardoq/hooks';
import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Tab } from './Tab';
import { ChevronOverflow, DropdownOverflow } from './TabOverflow';
import { OverflowType, TabListProps } from './types';
import { resolveActiveTabId } from './resolveActiveTabId';

const TabListWrapper = styled.div<{
  $overflowType: OverflowType;
}>`
  display: flex;
  flex-direction: column;
  ${({ $overflowType }) =>
    $overflowType === 'chevrons' &&
    css`
      flex: 0;
      width: 0;
      min-width: 100%;
    `}
  ${({ $overflowType }) =>
    $overflowType === 'dropdown' &&
    css`
      flex: 1;
    `}
`;

export const TAB_LIST_SELECTOR = TabListWrapper.toString();

export const TabList = <T extends string = string>({
  children,
  onTabChange,
  overflowType = 'chevrons',
  activeTabId,
  extraDropdownOptions,
  clickNamespace,
  fullButtons,
  moreButtonLabel,
  tablistAriaLabel,
}: TabListProps<T>) => {
  const rootWrapperRef = useRef(null);
  const { width: rootWrapperWidth = 0 } = useResizeObserver(rootWrapperRef);
  const resolvedActiveTabId = resolveActiveTabId(activeTabId, children);
  const labelsKey = children.map(child => child.props.label).join('|');

  const handleTabChange = (newTabId: string) => onTabChange(newTabId as T);

  const tabs = children.map(child => (
    <Tab
      key={child.props.tabId}
      {...child.props}
      isActive={resolvedActiveTabId === child.props.tabId}
      onClick={() => handleTabChange(child.props.tabId)}
    />
  ));

  return (
    <TabListWrapper
      ref={rootWrapperRef}
      $overflowType={overflowType}
      data-click-namespace={clickNamespace}
    >
      {overflowType === 'chevrons' ? (
        <ChevronOverflow
          key={labelsKey}
          rootWrapperWidth={rootWrapperWidth}
          fullButtons={fullButtons}
          tablistAriaLabel={tablistAriaLabel}
        >
          {tabs}
        </ChevronOverflow>
      ) : (
        <DropdownOverflow
          key={labelsKey}
          activeTabId={resolvedActiveTabId}
          onTabChange={handleTabChange}
          rootWrapperWidth={rootWrapperWidth}
          extraDropdownOptions={extraDropdownOptions}
          clickNamespace={clickNamespace}
          fullButtons={fullButtons}
          moreButtonLabel={moreButtonLabel}
          tablistAriaLabel={tablistAriaLabel}
        >
          {tabs}
        </DropdownOverflow>
      )}
    </TabListWrapper>
  );
};
